import { TeamName } from "@/components/TeamName";
import { useCalendarContext } from "@/features/calendar/CalendarProvider";
import { LightMode } from "@repo/skapa";
import { t } from "@repo/translation";
import {
  CoWorkerName,
  FlexDiv,
  LocalizedDateAndTime,
  LocalizedTime,
  Name,
  SimpleLabelField,
  translateAppointmentStatus,
  translateLabel,
  translateServices,
} from "@repo/ui";

import { CalendarItemTimeRange } from "../components/CalendarAppointment";
import { ScheduleActivityTitle } from "../components/ScheduleActivityTitle";
import {
  type CoWorkerCalendarItem,
  isCoWorkerCalendarBokaAppointment,
  isCoWorkerCalendarTask,
  isCoWorkerScheduleActivity,
} from "../types";

export const TooltipContent = ({ item }: { item: CoWorkerCalendarItem }) => {
  const { appointmentFilters } = useCalendarContext();
  const labelProps = {
    color: LightMode.colourTextAndIcon2,
    bold: true,
  };

  const renderCustomer = () =>
    item.customer?.firstName || item.customer?.lastName ? (
      <Name
        firstName={item.customer?.firstName}
        lastName={item.customer?.lastName}
      />
    ) : (
      "-"
    );

  const renderAssigned = () => {
    if (item.emailId && item.emailId !== "UNASSIGNED") {
      return <CoWorkerName email={item.emailId} />;
    }
    if (item.assignedTeam) {
      return <TeamName teamId={item.assignedTeam} />;
    }
    return t("label.unassigned");
  };

  const renderTime = () =>
    item.isDueBy ? (
      <LocalizedTime date={item.end} />
    ) : (
      <CalendarItemTimeRange
        fromDate={item.start}
        toDate={item.end}
        selectedDate={item.displayStart ?? appointmentFilters.selectedDate}
      />
    );

  const renderTitle = () =>
    isCoWorkerScheduleActivity(item) ? (
      <ScheduleActivityTitle item={item} />
    ) : (
      translateLabel(item.title)
    );
  return (
    <FlexDiv flexDirection="column" gap="4px">
      {item.title && (
        <SimpleLabelField
          label={t("label.title")}
          value={renderTitle()}
          {...labelProps}
        />
      )}
      {(item.customer?.firstName || item.customer?.lastName) && (
        <SimpleLabelField
          label={t("label.customer")}
          value={renderCustomer()}
          {...labelProps}
        />
      )}

      <SimpleLabelField
        label={t("label.time")}
        value={renderTime()}
        {...labelProps}
      />
      <SimpleLabelField
        label={t("label.assigned")}
        value={renderAssigned()}
        {...labelProps}
      />

      {isCoWorkerCalendarTask(item) && item.description && (
        <SimpleLabelField
          label={t("label.description")}
          value={item.description}
          {...labelProps}
        />
      )}

      {isCoWorkerCalendarBokaAppointment(item) && (
        <>
          <SimpleLabelField
            label={t("label.status")}
            value={translateAppointmentStatus(item.status)}
            {...labelProps}
          />
          <SimpleLabelField
            label={t("label.service")}
            value={translateServices(item.service)}
            {...labelProps}
          />
        </>
      )}

      {isCoWorkerScheduleActivity(item) && (
        <FlexDiv flexDirection="column" gap="2px">
          {item.editedAt && (
            <SimpleLabelField
              label={t("label.edited_at")}
              value={<LocalizedDateAndTime date={new Date(item.editedAt)} />}
              {...labelProps}
            />
          )}
          {item.editedBy && (
            <SimpleLabelField
              label={t("label.edited_by")}
              value={<CoWorkerName email={item.editedBy} />}
              {...labelProps}
            />
          )}
        </FlexDiv>
      )}
    </FlexDiv>
  );
};
